import type { ChildId } from './valueObjects/childId';
import type { MeasureDateTime } from './valueObjects/measureDateTime';
import type { MeasurerId } from './valueObjects/measurerId';
import type { MeasurerType } from './valueObjects/measurerType';
import type { Milk } from './valueObjects/milk';
import type { ParentId } from './valueObjects/parentId';
import type { MilkJsonPub, MilkJsonPvt } from '@/types/domain/milk/entities/milkEntity';

import {
  EntityPub,
} from '@/domain/entityPub';
import { DeleteType } from '@/domain/entityRepositoryBase';
import {
  EntityName,
} from '@/infra/appsync';

/**
 * ミルクプロパティプライベート
 */
export type MilkPropsPvt = {
  /** 測定日時 */
  measureDateTime: MeasureDateTime,
  /** 測定者ID */
  measurerId: MeasurerId,
  /** 測定タイプ */
  measurerType: MeasurerType,
  /** 親ID */
  parentId: ParentId,
  /** 子ID */
  childId: ChildId,
  /** ミルク */
  milk: Milk,
}

/**
 * ミルクプロパティ公開
 */
export type MilkPropsPub = Record<string, never>

/**
 * ミルクEntity
 */
export class MilkEntity extends EntityPub<
  MilkPropsPvt,
  MilkJsonPvt,
  MilkPropsPub,
  MilkJsonPub
> {
  /**
   * 削除タイプ
   */
  get deleteType(): DeleteType {
    return DeleteType.ParentChildId;
  }

  /**
   * 削除キー
   */
  get deleteKey(): string {
    return this.parentChildId;
  }

  toJsonData(): MilkJsonPvt {
    return {
      measureDateTime: this.measureDateTime.value.toJSON(),
      measurerId: this.measurerId.value,
      measurerType: this.measurerType.value,
      parentId: this.parentId.value,
      childId: this.childId.value,
      milk: this.milk.value,
    };
  }

  toJsonPubData(): MilkJsonPub {
    return {};
  }

  /**
   * Entity名
   */
  get entityName(): EntityName {
    const name: EntityName = EntityName.WagakoAppMilkMilkEntity;
    return name;
  }

  /**
   * Entityキー
   */
  get entityKey(): string {
    return `${this.measureDateTime.toStringDateTime()}#${this.parentId.value}_${this.childId.value}#${this.measurerType.value}#${this.measurerId.value}`;
  }

  /**
   * サブキー
   */
  get subKey(): string {
    return `${this.parentId.value}_${this.childId.value}#${this.measureDateTime.toStringDateTime()}#${this.measurerType.value}#${this.measurerId.value}`;
  }

  /**
   * 親子ID
   */
  get parentChildId(): string {
    return `${this.parentId.value}_${this.childId.value}`;
  }

  /**
   * 測定日時
   */
  get measureDateTime(): MeasureDateTime {
    return this._value.measureDateTime;
  }

  /**
   * 測定日時
   */
  set measureDateTime(measureDateTime: MeasureDateTime) {
    this._value.measureDateTime = measureDateTime;
  }

  /**
   * ミルク
   */
  get milk(): Milk {
    return this._value.milk;
  }

  /**
   * ミルク
   */
  set milk(milk: Milk) {
    this._value.milk = milk;
  }

  /**
   * 測定者ID
   */
  get measurerId(): MeasurerId {
    return this._value.measurerId;
  }

  /**
   * 測定者ID
   */
  set measurerId(measurerId: MeasurerId) {
    this._value.measurerId = measurerId;
  }

  /**
   * 測定タイプ
   */
  get measurerType(): MeasurerType {
    return this._value.measurerType;
  }

  /**
   * 測定タイプ
   */
  set measurerType(measurerType: MeasurerType) {
    this._value.measurerType = measurerType;
  }

  /**
   * 親ID
   */
  get parentId(): ParentId {
    return this._value.parentId;
  }

  /**
   * 親ID
   */
  set parentId(parentId: ParentId) {
    this._value.parentId = parentId;
  }

  /**
   * 子ID
   */
  get childId(): ChildId {
    return this._value.childId;
  }

  /**
   * 子ID
   */
  set childId(childId: ChildId) {
    this._value.childId = childId;
  }
}
